import { type ReactNode } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Text } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { YStack, XStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

import { Markdown } from './smoothdown';

export interface BannerProps {
  title: string;
  subtitle: string;
  onPrimaryAction?: () => void;
  onClose?: () => void;
  primaryActionText?: string;
  variant?: 'default' | 'info' | 'warning' | 'success' | 'danger';
  className?: string;
  actions?: ReactNode;
  showIcon?: boolean;
}

const variantIcons = {
  default: Icons.info,
  info: Icons.info,
  success: Icons.circleCheck,
  warning: Icons.warnTriangle,
  danger: Icons.warnOctagon,
} as const;

const variantIconColors = {
  default: '',
  info: 'text-blue-600 dark:text-blue-400',
  success: 'text-green-600 dark:text-green-400',
  warning: 'text-amber-600 dark:text-amber-400',
  danger: 'text-red-600 dark:text-red-400',
} as const;

const variantClasses = {
  default: 'bg-surface border-border',
  info: 'bg-blue-200/40 border-blue-300/40 dark:bg-blue-950/50 dark:border-blue-900/40',
  warning:
    'bg-amber-200/40 border-amber-300/40 dark:bg-amber-950/50 dark:border-amber-900/40',
  success:
    'bg-green-200/40 border-green-300/40 dark:bg-green-950/50 dark:border-green-900/40',
  danger:
    'bg-red-200/40 border-red-300/40 dark:bg-red-950/50 dark:border-red-900/40',
} as const;

export const Banner = ({
  title,
  subtitle,
  onPrimaryAction,
  onClose,
  primaryActionText = 'Action',
  variant = 'default',
  className,
  actions,
  showIcon = false,
}: BannerProps) => {
  const IconComponent = showIcon ? variantIcons[variant] : null;
  const closeOnly = !actions && !onPrimaryAction && !!onClose;

  return (
    <XStack
      className={cn(
        variantClasses[variant],
        'border',
        'gap-8',
        'rounded-lg',
        'pl-4 pr-3 py-3',
        closeOnly ? 'items-start' : 'items-center',
        'justify-between',
        className
      )}
    >
      <XStack className="gap-3 items-start flex-1">
        {IconComponent && (
          <XStack className="h-6 shrink-0 items-center">
            <IconComponent
              className={cn('size-4 mt-px', variantIconColors[variant])}
              aria-hidden="true"
            />
          </XStack>
        )}
        <YStack className="gap-0.5 flex-1">
          <Text className="font-semibold leading-6">{title}</Text>
          {subtitle && (
            <div className="text-sm text-foreground-subtle opacity-80">
              <Markdown
                content={subtitle}
                id={`banner-subtitle-${title}`}
                className="prose-sm max-w-none"
              />
            </div>
          )}
        </YStack>
      </XStack>
      <XStack className={cn('gap-3 items-center', closeOnly && 'h-6')}>
        {actions || (
          <>
            {onPrimaryAction && (
              <Button onClick={onPrimaryAction} size="sm">
                {primaryActionText}
              </Button>
            )}
            {onClose && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                className="rounded-full"
              >
                <Icons.cancel className="size-4" />
              </Button>
            )}
          </>
        )}
      </XStack>
    </XStack>
  );
};
